import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Button, Alert } from 'design-system';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import { DOLPHIN_MISC } from '@scale/shared/defaults';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import API from '@scale/frontend-lib/dashboard-api';
import { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';

const MIN_GENERATE_INTERVAL_IN_SECS = 10;

interface Props {
  readOnly: boolean;
  projectId?: string;
  assignmentId?: string;
  currentFields: TextCollectionField[];
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  setAnnotationFunctions: Record<string, (annotation: TextCollectionAnnotation) => void>;
}

const GenerationButton: React.FC<Props> = props => {
  const {
    readOnly,
    projectId,
    assignmentId,
    currentFields,
    currentAnnotations,
    setAnnotationFunctions,
  } = props;

  const [countDown, setCountDown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [promptError, setPromptError] = React.useState('');

  const generatedResponseAField = currentFields.find(
    field => field.field_id === DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A,
  );
  const generatedResponseBField = currentFields.find(
    field => field.field_id === DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_B,
  );

  if (!generatedResponseAField || !generatedResponseBField) {
    throw new Error('Cannot find generated fields');
  }

  const setResponseAFn = setAnnotationFunctions[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A];
  const setResponseBFn = setAnnotationFunctions[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_B];
  const prompt = _.get(currentAnnotations, 'prompt.response[0]', '') as string;
  const language = _.get(currentAnnotations, 'Language.response[0][0]', '') as string;
  const canGenerate = !readOnly && prompt.length > 0 && countDown === 0 && !loading;

  const usePromptValidation = useUnleashBoolean(
    'dolphin-prompt-validation',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const handleGenerate = async () => {
    setPromptError('');
    setLoading(true);

    if (usePromptValidation) {
      const { errors } = await API.selfserve.validatePrompt({
        projectId: projectId || '',
        promptText: prompt,
        chatBoxText: DOLPHIN_MISC.DOLPHIN_PROMPT_FIELD,
        assignmentId,
      });
      if (errors.length > 0) {
        setPromptError(errors[0]);
        setLoading(false);
        return;
      }
    }

    const response = await API.selfserve.dolphinGenerateResponse({
      assignmentId,
      prompt,
      language,
    });

    const annotationsA = getDefaultAnnotation(generatedResponseAField);
    const annotationsB = getDefaultAnnotation(generatedResponseBField);

    annotationsA.response[0] = response[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A];
    annotationsB.response[0] = response[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_B];

    setResponseAFn(annotationsA);
    setResponseBFn(annotationsB);

    setCountDown(MIN_GENERATE_INTERVAL_IN_SECS);
    setLoading(false);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (countDown > 0) {
        setCountDown(prev => prev - 1);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  });

  return (
    <div style={{ margin: '8px' }}>
      {promptError && (
        <Alert style={{ marginBottom: '8px' }} severity="error">
          {promptError}
        </Alert>
      )}
      <Button disabled={!canGenerate} type="primary" onClick={handleGenerate}>
        {`Generate Responses ${countDown !== 0 ? countDown : ''}`}
      </Button>
    </div>
  );
};

export default GenerationButton;
